import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import TwoWheelerRoundedIcon from "@mui/icons-material/TwoWheelerRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useMaterialUIController } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";

const SIDENAV_WIDTH = 264;
const SIDENAV_BG = "#1A2B4C";
const SIDENAV_BG_DEEP = "#142340";
const SIDENAV_TEXT = "#D2DBEB";
const SIDENAV_TEXT_MUTED = "#9AA8C2";
const SIDENAV_ACTIVE_BG = "rgba(255,255,255,0.14)";
const SIDENAV_ACTIVE_FG = "#FFFFFF";
const SIDENAV_ACTIVE_ACCENT = "rgba(255,255,255,0.55)";
const SIDENAV_HOVER_BG = "rgba(255,255,255,0.08)";

function MenuItem({ route, pathname, level, openGroups, onToggle }) {
  const isParent = Array.isArray(route.collapse) && route.collapse.length > 0;
  const isOpen = openGroups[route.key] || false;
  const isActive = pathname === route.route || pathname.startsWith(`${route.route}/`);
  const paddingLeft = 1.6 + level * 1.4;

  if (!isParent) {
    return (
      <ListItemButton
        component={NavLink}
        to={route.route}
        sx={{
          position: "relative",
          pl: paddingLeft,
          pr: 1.4,
          py: 0.85,
          borderRadius: 1.4,
          mb: 0.4,
          color: isActive ? SIDENAV_ACTIVE_FG : SIDENAV_TEXT,
          bgcolor: isActive ? SIDENAV_ACTIVE_BG : "transparent",
          transition: "all 0.18s ease",
          "&::before": isActive
            ? {
                content: '""',
                position: "absolute",
                left: 6,
                top: 7,
                bottom: 7,
                width: 3,
                borderRadius: 2,
                bgcolor: SIDENAV_ACTIVE_ACCENT,
              }
            : undefined,
          "& .MuiListItemText-primary": {
            fontSize: "0.92rem",
            fontWeight: isActive ? 700 : 500,
            letterSpacing: "0.005em",
          },
          "&:hover": {
            bgcolor: isActive ? SIDENAV_ACTIVE_BG : SIDENAV_HOVER_BG,
            color: "#FFFFFF",
          },
        }}
      >
        {level > 0 && (
          <FiberManualRecordRoundedIcon
            sx={{
              fontSize: 6,
              color: isActive ? SIDENAV_ACTIVE_FG : SIDENAV_TEXT_MUTED,
              mr: 1.1,
            }}
          />
        )}
        <ListItemText primary={route.name} />
      </ListItemButton>
    );
  }

  return (
    <>
      <ListItemButton
        onClick={() => onToggle(route.key)}
        sx={{
          pl: paddingLeft,
          pr: 1.2,
          py: 0.95,
          borderRadius: 1.4,
          mb: 0.4,
          color: isActive || isOpen ? "#FFFFFF" : SIDENAV_TEXT,
          bgcolor: isActive || isOpen ? SIDENAV_HOVER_BG : "transparent",
          transition: "all 0.18s ease",
          "& .MuiListItemText-primary": {
            fontSize: "0.95rem",
            fontWeight: 600,
            textTransform: "none",
          },
          "&:hover": {
            bgcolor: SIDENAV_HOVER_BG,
            color: "#FFFFFF",
          },
        }}
      >
        <ListItemText primary={route.name} />
        {isOpen ? (
          <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: SIDENAV_TEXT_MUTED }} />
        ) : (
          <ChevronRightRoundedIcon sx={{ fontSize: 18, color: SIDENAV_TEXT_MUTED }} />
        )}
      </ListItemButton>

      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            ml: 1.2,
            pl: 0.4,
            borderLeft: "1px solid rgba(255,255,255,0.10)",
          }}
        >
          {route.collapse
            .filter((child) => child?.type === "collapse" && child?.route && child.route !== "/login")
            .map((child) => (
              <MenuItem
                key={child.key}
                route={child}
                pathname={pathname}
                level={level + 1}
                openGroups={openGroups}
                onToggle={onToggle}
              />
            ))}
        </List>
      </Collapse>
    </>
  );
}

export default function SimpleSidenav({ routes }) {
  const [controller] = useMaterialUIController();
  const { pathname } = useLocation();
  const { usuarioLog } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [pathname, isMobile]);

  const visibleRoutes = useMemo(() => {
    const isPublicRoute = (moduleKey) =>
      moduleKey === undefined || moduleKey === null || String(moduleKey).trim() === "";

      
    const normalizeRoutes = (currentLevelRoutes = []) => {
      const candidateRoutes = currentLevelRoutes.filter(
        (route) => route?.type === "collapse" && route?.route && route.route !== "/login"
      );

      const mappedRoutes = candidateRoutes.map((route) => {
        const visibleChildren = Array.isArray(route.collapse) ? normalizeRoutes(route.collapse) : [];

        if (visibleChildren.length > 0) {
          return { ...route, collapse: visibleChildren };
        }

        // `programa` permite que una entrada comparta permiso con otra (misma
        // fila de rolesusuarios) manteniendo su `key` único para React/App.
        const moduleKey = route?.programa ?? route?.key;

        const hasAccess = isPublicRoute(moduleKey) || tienePermisosModulo(controller.roleDetalles, moduleKey);

        return hasAccess ? { ...route, collapse: [] } : null;
      });

      return mappedRoutes.filter(Boolean);
    };

    return normalizeRoutes(routes);
  }, [routes, controller.roleDetalles]);

  const usuarioActual = usuarioLog || "Usuario";
  const usuarioInicial = usuarioActual ? usuarioActual.toString().trim().charAt(0).toUpperCase() : "U";
  const usuarioEmail = controller?.emailLog || `${usuarioActual.toLowerCase().replace(/\s+/g, ".")}@suitorbit.com`;

  const [openGroups, setOpenGroups] = useState(() => {
    const getOpenGroups = (items = [], current = {}) => {
      items.forEach((route) => {
        if (Array.isArray(route.collapse) && route.collapse.length > 0) {
          const hasActiveChild = route.collapse.some(
            (child) => pathname === child.route || pathname.startsWith(`${child.route}/`)
          );

          if (hasActiveChild || pathname.startsWith(route.route)) {
            current[route.key] = true;
          }

          getOpenGroups(route.collapse, current);
        }
      });
      return current;
    };

    return getOpenGroups(visibleRoutes);
  });

  useEffect(() => {
    const getOpenGroups = (items = [], current = {}) => {
      items.forEach((route) => {
        if (Array.isArray(route.collapse) && route.collapse.length > 0) {
          const hasActiveChild = route.collapse.some(
            (child) => pathname === child.route || pathname.startsWith(`${child.route}/`)
          );

          if (hasActiveChild || pathname.startsWith(route.route)) {
            current[route.key] = true;
          }

          getOpenGroups(route.collapse, current);
        }
      });
      return current;
    };

    setOpenGroups((prev) => ({ ...prev, ...getOpenGroups(visibleRoutes) }));
  }, [pathname, visibleRoutes]);

  const handleToggle = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSignOut = () => {
    localStorage.clear();
  };

  const sidenavContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background: `linear-gradient(180deg, ${SIDENAV_BG} 0%, ${SIDENAV_BG_DEEP} 100%)`,
        color: SIDENAV_TEXT,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2.4,
          pt: 2.4,
          pb: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.4,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 1.4,
            bgcolor: "#FFFFFF",
            color: SIDENAV_BG,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <TwoWheelerRoundedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              color: "#FFFFFF",
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
            }}
          >
            SuitOrbit
          </Typography>
          <Typography
            sx={{
              color: SIDENAV_TEXT_MUTED,
              fontSize: "0.72rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
            }}
          >
            Motoprestamo
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={() => setMobileOpen(false)}
            sx={{
              color: SIDENAV_TEXT_MUTED,
              "&:hover": { color: "#FFFFFF", bgcolor: "rgba(255,255,255,0.06)" },
            }}
            aria-label="Cerrar menú"
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255,255,255,0.18)",
              borderRadius: 10,
            },
          }}
        >
          <List sx={{ p: 1.2 }}>
            {visibleRoutes.map((route) => (
              <MenuItem
                key={route.key}
                route={route}
                pathname={pathname}
                level={0}
                openGroups={openGroups}
                onToggle={handleToggle}
              />
            ))}
          </List>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 1.2, mt: 0.5 }} />

        <Box
          sx={{
            mx: 1.2,
            mt: 1,
            mb: 0.6,
            p: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            borderRadius: 1.4,
            bgcolor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: "#1B6CFB",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.92rem",
            }}
          >
            {usuarioInicial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontSize: "0.86rem",
                fontWeight: 700,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {usuarioActual}
            </Typography>
            <Typography
              sx={{
                color: SIDENAV_TEXT_MUTED,
                fontSize: "0.72rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {usuarioEmail}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 1.2, pt: 0.5 }}>
          <ListItemButton
            component={NavLink}
            to="/login"
            onClick={handleSignOut}
            sx={{
              py: 0.9,
              px: 1.4,
              borderRadius: 1.4,
              color: "#FFB7C8",
              bgcolor: "rgba(225,29,72,0.08)",
              border: "1px solid rgba(225,29,72,0.2)",
              "& .MuiListItemText-primary": { fontSize: "0.9rem", fontWeight: 600 },
              "&:hover": {
                bgcolor: "rgba(225,29,72,0.16)",
                color: "#FFFFFF",
              },
            }}
          >
            <LogoutRoundedIcon sx={{ fontSize: 18, mr: 1.2 }} />
            <ListItemText primary="Sign out" />
          </ListItemButton>
        </Box>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menú"
          sx={{
            position: "fixed",
            top: 10,
            left: 10,
            zIndex: (t) => t.zIndex.drawer + 2,
            width: 42,
            height: 42,
            borderRadius: 1.4,
            bgcolor: SIDENAV_BG,
            color: "#FFFFFF",
            boxShadow: "0 6px 18px rgba(15,23,42,0.25)",
            display: mobileOpen ? "none" : "inline-flex",
            "&:hover": { bgcolor: SIDENAV_BG_DEEP },
          }}
        >
          <MenuRoundedIcon />
        </IconButton>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "84vw", sm: SIDENAV_WIDTH },
              maxWidth: SIDENAV_WIDTH + 20,
              border: "none",
              backgroundColor: "transparent",
            },
          }}
        >
          {sidenavContent}
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        width: SIDENAV_WIDTH,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
      }}
    >
      {sidenavContent}
    </Box>
  );
}
